import { useCallback, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { GroupProfileData, GroupTypes } from '@Types';
import { useSetFieldErrors } from '@Hooks';
import { useGetMatchmakingApplyFundNonProfitLinks, useUpdateGroupProfile } from '@Hooks/Api';
import { Loader } from '@Components';
import { useNotificationsContext } from '@Contexts';
import { useRouter } from '@Helpers';
import isServer from '@Helpers/isServer';
import { Routes, RoutingPaths } from '@App/paths';

import { useFormData } from './useFormData';
import { Profile } from '../Profile';
import { useProfileTypes } from '../useProfileTypes';
import { setBackendFieldErrors } from '@Helpers/setBackendFieldErrors';

const TYPES = Object.keys(GroupTypes);

export const GroupProfileController = ({ forceNoProfile = false, organizationName = '' }) => {
  const { t } = useTranslation('createAccount');
  const methods = useForm<GroupProfileData>();
  const { setError, reset } = methods;
  const [{ data: dataGroup, loading: loadingGroup, isError: noGroup }] = useFormData(
    forceNoProfile,
    organizationName,
  );
  const [updateGroupProfile, response] = useUpdateGroupProfile(noGroup);
  const { error, loading: isSaving } = response;
  const { push, params } = useRouter();
  const typeList = useProfileTypes('group_types', TYPES);
  const { error: errorNotification } = useNotificationsContext();
  const nextUrl = params.next as string | undefined;

  const [
    getMatchmakingApplyFundNonProfitLinks,
    {
      loading: isGetMatchmakingApplyFundNonProfitLinksLoading,
      data: applyMatchmakingApplyFundNonProfitLinksData,
    },
  ] = useGetMatchmakingApplyFundNonProfitLinks();

  useEffect(() => {
    getMatchmakingApplyFundNonProfitLinks();
  }, [getMatchmakingApplyFundNonProfitLinks]);

  useEffect(() => {
    if (!loadingGroup && dataGroup) {
      reset(dataGroup);
    }
  }, [reset, loadingGroup, dataGroup]);

  const onSubmit = useCallback(
    (data: GroupProfileData) => {
      const formattedData = {
        ...data,
        company_number: data.company_number || undefined,
        charity_number: data.charity_number || undefined,
        other_registration_number: data.other_registration_number || undefined,
      };
      updateGroupProfile(formattedData)
        .then(() => {
          if (noGroup && !isServer && window.dataLayer) {
            window.dataLayer.push({ event: 'groupCreateProfile' });
          }
          // Only redirect to project create if they are setting up their profile for the first time and have an apply fund non-profit link
          if (Boolean(applyMatchmakingApplyFundNonProfitLinksData?.results?.length) && noGroup) {
            push(Routes.PROJECT_CREATE);
            return; // Exit early to prevent other pushes
          }
          if (nextUrl) {
            push(nextUrl);
          } else {
            push(RoutingPaths.DASHBOARD_GROUP);
          }
        })
        .catch(({ data }) => {
          if (data?.field_errors) {
            setBackendFieldErrors(data.field_errors, setError);
          } else {
            errorNotification(t('common:errors.generic_error'));
          }
        });
    },
    [
      updateGroupProfile,
      noGroup,
      errorNotification,
      push,
      nextUrl,
      t,
      setError,
      applyMatchmakingApplyFundNonProfitLinksData,
    ],
  );

  const onCancel = useCallback(() => {
    if (nextUrl) {
      push(nextUrl);
    } else {
      push(RoutingPaths.DASHBOARD_GROUP);
    }
  }, [push, nextUrl]);

  useSetFieldErrors({
    fieldErrors: error?.field_errors,
    setError,
    loading: loadingGroup,
  });

  if (!forceNoProfile && (loadingGroup || !dataGroup)) return <Loader />;

  return (
    <FormProvider {...methods}>
      <Profile
        isFunder={false}
        onSubmit={methods.handleSubmit(onSubmit)}
        onCancel={onCancel}
        isSaving={isSaving}
        typeList={typeList}
        createMode={noGroup}
        headerDisclaimer={t('group_profile.header_disclaimer')}
        focusAreaDisclaimer={
          <Trans
            ns="createAccount"
            i18nKey="group_profile.focus_areas_disclaimer"
            components={[<strong />]}
          />
        }
      />
    </FormProvider>
  );
};
